import React from 'react';

interface TimelineStep {
  status: string;
  label: string;
  timestamp?: string;
  description?: string;
  icon: string;
}

interface ErrandTimelineProps {
  currentStatus: string;
  createdAt: string;
  acceptedAt?: string;
  inProgressAt?: string;
  pickedUpAt?: string;
  completedAt?: string;
  cancelledAt?: string;
}

const ErrandTimeline: React.FC<ErrandTimelineProps> = ({
  currentStatus,
  createdAt,
  acceptedAt,
  inProgressAt,
  pickedUpAt,
  completedAt,
  cancelledAt
}) => {
  const getTimelineSteps = (): TimelineStep[] => {
    const steps: TimelineStep[] = [
      {
        status: 'pending',
        label: 'Request Submitted',
        timestamp: createdAt,
        description: 'Your errand request has been submitted',
        icon: '📝'
      },
      {
        status: 'accepted',
        label: 'Accepted by Driver',
        timestamp: acceptedAt,
        description: 'A driver has accepted your errand',
        icon: '✅'
      },
      {
        status: 'in_progress',
        label: 'In Progress',
        timestamp: inProgressAt,
        description: 'Driver is working on your errand',
        icon: '🚚'
      },
      {
        status: 'picked_up',
        label: 'Picked Up',
        timestamp: pickedUpAt,
        description: 'Items have been picked up',
        icon: '📦'
      },
      {
        status: 'completed',
        label: 'Completed',
        timestamp: completedAt,
        description: 'Errand has been completed',
        icon: '🎉'
      }
    ];

    // Add cancelled step if applicable
    if (currentStatus === 'cancelled' || cancelledAt) {
      steps.push({
        status: 'cancelled',
        label: 'Cancelled',
        timestamp: cancelledAt,
        description: 'Errand was cancelled',
        icon: '❌'
      });
    }

    return steps;
  };

  const steps = getTimelineSteps();
  const currentStepIndex = steps.findIndex(step => step.status === currentStatus);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-BW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStepStatus = (index: number): 'completed' | 'active' | 'pending' => {
    if (index < currentStepIndex) return 'completed';
    if (index === currentStepIndex) return 'active';
    return 'pending';
  };

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Errand Progress</h3>
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Timeline steps */}
        <div className="space-y-6">
          {steps.map((step, index) => {
            const stepStatus = getStepStatus(index);
            const isCompleted = stepStatus === 'completed';
            const isActive = stepStatus === 'active';
            const isPending = stepStatus === 'pending';

            return (
              <div key={step.status} className="relative flex items-start">
                {/* Icon */}
                <div
                  className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isActive
                      ? 'bg-green-600 text-white ring-4 ring-green-200'
                      : 'bg-gray-200 text-gray-500'
                  } transition-all duration-300`}
                >
                  <span className="text-sm">{step.icon}</span>
                </div>

                {/* Content */}
                <div className="ml-4 flex-1 pb-6">
                  <div
                    className={`flex items-center justify-between ${
                      isCompleted || isActive ? 'text-gray-900' : 'text-gray-500'
                    }`}
                  >
                    <h4 className={`text-sm font-medium ${isActive ? 'text-green-600' : ''}`}>
                      {step.label}
                    </h4>
                    {step.timestamp && (
                      <span className="text-xs text-gray-500">{formatDate(step.timestamp)}</span>
                    )}
                  </div>
                  {step.description && (
                    <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ErrandTimeline;



